import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Mail, Lock, Loader2, Eye, EyeOff } from 'lucide-react';
import { useApiWakeUp } from '../hooks/useApiWakeUp';
import { WakingOverlay } from '../components/WakingOverlay';

const AdminLogin = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const apiStatus = useApiWakeUp();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Trim whitespace to prevent copy-paste errors
      await login(email.trim(), password.trim());
      navigate('/admin');
    } catch (err) {
      console.error('Login error:', err);
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') {
        setError(detail);
      } else if (Array.isArray(detail)) {
        setError(detail.map(e => e.msg || JSON.stringify(e)).join(' '));
      } else {
        setError('Invalid email or password');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F1E8] flex items-center justify-center px-4">
      {apiStatus === 'waking' && <WakingOverlay />}
      <Card className="w-full max-w-md border-2 border-[#8B7355]/20">
        <CardHeader className="text-center">
          <div className="mb-6">
            <img 
              src="https://customer-assets.emergentagent.com/job_6bd1bdb6-929b-492c-804f-03ddd69ea176/artifacts/udp9h1gt_SHOT%20TO%20THE%20NINES.png" 
              alt="Shot to the Nines" 
              className="h-20 mx-auto object-contain mb-4"
            />
          </div>
          <CardTitle className="text-3xl font-serif text-[#8B7355] italic">Admin Login</CardTitle>
          <p className="text-[#8B7355]/70 mt-2">Access your dashboard</p>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
            <div>
              <Label htmlFor="email" className="flex items-center gap-2 text-[#8B7355] mb-2">
                <Mail className="w-4 h-4" /> Email
              </Label>
              <Input
                id="email"
                name="admin-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="off"
                className="border-[#8B7355]/30"
                placeholder="your@email.com"
                data-testid="admin-login-email"
              />
            </div>

            <div>
              <Label htmlFor="password" className="flex items-center gap-2 text-[#8B7355] mb-2">
                <Lock className="w-4 h-4" /> Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  name="admin-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  className="border-[#8B7355]/30 pr-12"
                  placeholder="••••••••"
                  data-testid="admin-login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute inset-y-0 right-0 px-3 flex items-center text-[#8B7355]/70 hover:text-[#8B7355]"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  data-testid="admin-login-toggle-pw"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#8B7355] hover:bg-[#6B5845] text-white py-6 rounded-none font-serif text-lg"
              data-testid="admin-login-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Logging in...
                </>
              ) : (
                'Login'
              )}
            </Button>

            <div className="text-center pt-2">
              <Link
                to="/admin/forgot-password"
                className="text-sm text-[#8B7355]/70 hover:text-[#C9A961]"
                data-testid="admin-forgot-password-link"
              >
                Forgot your password?
              </Link>
            </div>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => navigate('/')}
              className="text-[#8B7355]/70 hover:text-[#8B7355] text-sm transition-colors"
            >
              ← Back to Home
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLogin;
